import { useEffect, useState } from "react";

/**
 * Cercle qui pulse selon le rythme respiratoire (inhale/hold/exhale en secondes).
 * Respecte prefers-reduced-motion : opacité douce au lieu de scale.
 */
export default function BreathingOrb({ rhythm = { inhale: 4, hold: 2, exhale: 6 }, active = true, size = 240 }) {
  const [phase, setPhase] = useState("prepare");
  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const total = (rhythm.inhale + (rhythm.hold || 0) + rhythm.exhale) * 1000;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timers = [];
    function cycle() {
      if (cancelled) return;
      setPhase("inhale");
      timers.push(setTimeout(() => { if (!cancelled) setPhase(rhythm.hold ? "hold" : "exhale"); }, rhythm.inhale * 1000));
      if (rhythm.hold) {
        timers.push(setTimeout(() => { if (!cancelled) setPhase("exhale"); }, (rhythm.inhale + rhythm.hold) * 1000));
      }
      timers.push(setTimeout(() => { if (!cancelled) cycle(); }, total));
    }
    cycle();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [active, rhythm.inhale, rhythm.hold, rhythm.exhale, total]);

  const scale = reduced ? 1 : phase === "inhale" ? 1.18 : phase === "hold" ? 1.18 : 0.9;
  const opacity = reduced ? (phase === "exhale" ? 0.6 : 0.95) : 1;
  const transitionDuration = phase === "inhale" ? `${rhythm.inhale}s` : phase === "exhale" ? `${rhythm.exhale}s` : `${rhythm.hold || 0}s`;
  const label = phase === "inhale" ? "Inspirez" : phase === "hold" ? "Retenez" : phase === "exhale" ? "Expirez" : "Prêt";

  return (
    <div className="relative flex items-center justify-center" data-testid="breathing-orb" style={{ width: size, height: size }}>
      {/* Halo extérieur diffus */}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 1.15, height: size * 1.15,
          background: "radial-gradient(circle at 50% 50%, rgba(178,197,188,0.20), rgba(136,160,150,0.06) 40%, transparent 65%)",
          filter: "blur(8px)",
        }}
      />
      {/* Orbe principal qui pulse */}
      <div
        className="absolute rounded-full"
        style={{
          width: size, height: size,
          transform: `scale(${scale})`,
          opacity,
          transition: `transform ${transitionDuration} cubic-bezier(0.4, 0, 0.2, 1), opacity 1200ms ease-in-out`,
          background: "radial-gradient(circle at 35% 30%, rgba(220,232,225,0.85), rgba(178,197,188,0.55) 30%, rgba(136,160,150,0.30) 55%, rgba(74,107,93,0.10) 78%, transparent 88%)",
          boxShadow: "0 0 90px rgba(178,197,188,0.35), inset 0 0 50px rgba(220,232,225,0.25), inset 0 -10px 30px rgba(74,107,93,0.20)",
        }}
      />
      <div className="relative z-10 text-center">
        <p className="font-serif text-2xl text-[color:var(--bg-deep)] drop-shadow-sm" data-testid="breathing-phase">{label}</p>
      </div>
    </div>
  );
}
